import { useState, useRef } from "react";
import {
  ChevronDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { CheckIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { useClickOutside } from "hooks";
import clsx from "clsx";

export function FilterDropdown({
  filterKey,
  options = [],
  selectedValue,
  onSelect,
  onClear,
  label,
  placeholder = "Select Here",
  showAllOption = true,
  showSearch = true,
  searchThreshold = 5,
  className = "",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setIsOpen(false));

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const getFilteredOptions = (options, searchTerm) => {
    if (!searchTerm) return options || [];
    return (options || []).filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const getFilterDisplayValue = (selectedValue, options) => {
    if (!selectedValue || selectedValue === "all") return placeholder;

    const option = options?.find((opt) => opt.id === selectedValue);
    return option?.label || selectedValue;
  };

  const filteredOptions = getFilteredOptions(options, searchTerm);
  const shouldShowSearch =
    showSearch && options && options.length > searchThreshold;

  const handleSelect = (value) => {
    onSelect?.(filterKey, value);
    setIsOpen(false);
    setSearchTerm(""); // Reset search term
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onClear?.(filterKey);
  };

  return (
    <div className={clsx("relative", className)} ref={dropdownRef}>
      {label && (
        <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-300">
          {label}
        </label>
      )}

      <div className="relative">
        <Button
          variant="outlined"
          disabled={disabled}
          className={clsx(
            "flex w-full items-center justify-between gap-2 border-gray-300 bg-white px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700",
            selectedValue &&
              selectedValue !== "all" &&
              "border-primary-500 bg-primary-50 dark:bg-primary-900/20",
            disabled && "cursor-not-allowed opacity-60 bg-gray-50 dark:bg-dark-900"
          )}
          onClick={() => !disabled && toggleDropdown()}
        >

          <span
            className={clsx(
              "truncate",
              selectedValue && selectedValue !== "all"
                ? "text-primary-700 dark:text-primary-300 font-medium"
                : "text-gray-500 dark:text-gray-400",
            )}
          >
            {getFilterDisplayValue(selectedValue, options)}
          </span>
          <div className="flex items-center gap-1">
            {selectedValue && selectedValue !== "all" && (
              <XCircleIcon
                className="size-4 text-gray-400 transition-colors hover:text-red-500"
                onClick={handleClear}
              />
            )}
            <ChevronDownIcon
              className={clsx(
                "size-4 transition-transform",
                isOpen && "rotate-180",
              )}
            />
          </div>
        </Button>

        {isOpen && (
          <div className="absolute z-50 mt-1 max-h-64 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
            {/* Search */}
            {shouldShowSearch && (
              <div className="border-b border-gray-200 p-2 dark:border-gray-600">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
                  <input
                    type="text"
                    placeholder={`Search ${typeof label === 'string' ? label.toLowerCase() : "options"}...`}
                    className="focus:ring-primary-500 w-full rounded-md border border-gray-200 bg-gray-50 py-2 pr-3 pl-9 text-sm text-gray-900 focus:ring-2 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {/* Options */}
            <div className="max-h-48 overflow-y-auto">
              {/* All option */}
              {showAllOption && (
                <button
                  className={clsx(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700",
                    (!selectedValue || selectedValue === "all") &&
                      "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300",
                  )}
                  onClick={() => handleSelect("all")}
                >
                  {(!selectedValue || selectedValue === "all") && (
                    <CheckIcon className="text-primary-600 size-4" />
                  )}
                  <span
                    className={
                      !selectedValue || selectedValue === "all"
                        ? "ml-0"
                        : "ml-6"
                    }
                  >
                    All {label}
                  </span>
                </button>
              )}

              {/* Filtered options */}
              {filteredOptions.map((option) => (
                <button
                  key={option.id}
                  className={clsx(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700",
                    selectedValue === option.id &&
                      "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300",
                  )}
                  onClick={() => handleSelect(option.id)}
                >
                  {selectedValue === option.id && (
                    <CheckIcon className="text-primary-600 size-4" />
                  )}
                  <span
                    className={selectedValue === option.id ? "ml-0" : "ml-6"}
                  >
                    {option.label}
                    {option.count !== undefined && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({option.count})
                      </span>
                    )}
                  </span>
                </button>
              ))}

              {filteredOptions.length === 0 && searchTerm && (
                <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No options found for `${searchTerm}`
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
